import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  formatFractionInput,
  formatPercentageInput,
  isNullOrUndefined,
  isValidPercentageDecimal,
  queryIsLoading,
  useGetDairyManureStorage,
  useGetFootprintingDairyManureStorage,
  useGetFootprintingDairyPrimaryDataById,
  useKeyedFormList,
  useMutateFootprintingDairyManureStorage,
  useMutateFootprintingDairyPrimaryData,
  useTranslation,
} from "utils";

import { DAIRY_PRACTICE_SLUG, UploadableManureStorage } from "models";

import {
  Button,
  Card,
  Flex,
  Form,
  FormInstance,
  Icon,
  IconButton,
  InputNumber,
  Option,
  PageSpin,
  ProgressBarCardWithContent,
  Select,
  Typography,
} from "components";

interface FarmGeneralProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
}

const manureStoragePlaceholder: UploadableManureStorage = {
  id: undefined,
  manureStorageId: "",
  amountStoredPct: null,
};

const ManureStorageForm = ({ primaryDataId, readOnly, farmId, year }: FarmGeneralProps) => {
  const { t } = useTranslation(["footprintingDairy", "dairy"]);
  const formRef = useRef<FormInstance>(null);

  const [amountStoredGasTankPct, setAmountStoredGasTankPct] = useState<number | null>(null);

  const manureStorageUsageFullQuery = useGetFootprintingDairyManureStorage(primaryDataId);
  const { data: ManureStorageData } = manureStorageUsageFullQuery;
  const dairyManureStorageFullQuery = useGetDairyManureStorage();
  const { data: dairyManureStorageQuery } = dairyManureStorageFullQuery;

  const dairyPrimaryDataFullQuery = useGetFootprintingDairyPrimaryDataById(primaryDataId ?? "");
  const { data: dairyPrimaryDataQuery, isSuccess } = dairyPrimaryDataFullQuery;
  const isLoading = [manureStorageUsageFullQuery, dairyManureStorageFullQuery, dairyPrimaryDataFullQuery].some(queryIsLoading);

  const keyedManureStorage = useKeyedFormList<UploadableManureStorage>(formRef, { isReady: !isLoading });
  const { mutate: mutateDairyPrimaryData } = useMutateFootprintingDairyPrimaryData(
    farmId,
    year,
    {
      farmId,
      year,
      amountStoredGasTankPct: amountStoredGasTankPct,
      isSubmitted: false,
    },
    primaryDataId
  );

  const selectGroupName = "footprinting-dairy-manure-storage-select";

  const { mutate: mutateDairyManureStorage } = useMutateFootprintingDairyManureStorage(
    farmId,
    year,
    {
      footprintingFarmPrimaryDataId: primaryDataId ?? "",
      selectedManureStorage: keyedManureStorage.payload.filter((m) => !!m.manureStorageId),
    },
    primaryDataId
  );

  // Save on unmount
  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateDairyManureStorage();
        mutateDairyPrimaryData({});
      }
    },
    [isLoading, mutateDairyManureStorage, mutateDairyPrimaryData, readOnly]
  );

  // Load manure management data
  useEffect(() => {
    if (ManureStorageData) {
      keyedManureStorage.actions.hydrate(
        ManureStorageData.length
          ? ManureStorageData.map((m) => ({
              id: m.id,
              manureStorageId: m.manureStorageId ?? "",
              amountStoredPct: m.amountStoredPct ?? null,
            }))
          : [manureStoragePlaceholder]
      );
    }
  }, [ManureStorageData, keyedManureStorage.actions]);

  useEffect(() => {
    if (dairyPrimaryDataQuery && isSuccess) {
      setAmountStoredGasTankPct(dairyPrimaryDataQuery.amountStoredGasTankPct ?? null);

      formRef.current?.setFieldsValue({
        amountStoredGasTankPct: formatFractionInput(dairyPrimaryDataQuery.amountStoredGasTankPct ?? null),
      });

      const fieldsToValidate = (["amountStoredGasTankPct"] as const).some(
        (field) => dairyPrimaryDataQuery[field] !== null && dairyPrimaryDataQuery[field] !== undefined
      );

      if (fieldsToValidate) {
        keyedManureStorage.actions.requestRehydrationValidation();
      }
    }
  }, [dairyPrimaryDataQuery, isSuccess, keyedManureStorage.actions]);

  const totalStoragePercentage = useMemo(
    () => keyedManureStorage.entries.reduce((total, storage) => total + (formatFractionInput(storage.amountStoredPct) || 0), 0),
    [keyedManureStorage.entries]
  );

  const hasLoadedManureStorage = keyedManureStorage.entries.length > 0 && ManureStorageData !== undefined;

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <Form ref={formRef} preserve={false}>
      <Flex vertical gap={16}>
        <Card border dataTestId="footprinting-dairy-manure-storage-form" className="padding-200">
          <Flex vertical gap={16}>
            <Flex vertical>
              <Typography variant="h3">{t("dairyManureStorageForm.header")}</Typography>
              <Typography variant="paragraph-m" color="grey-600" className="margin-top-25">
                {t("dairyManureStorageForm.description")}
              </Typography>
            </Flex>

            {hasLoadedManureStorage && (
              <ProgressBarCardWithContent
                currentValue={totalStoragePercentage}
                title={t("dairyManureStorageForm.progressBar.title")}
                // description={t("dairyManureStorageForm.progressBar.description")} // design decision to comment this out for now.
                errorMessage={t("dairyManureStorageForm.progressBar.errorMessage")}
                successMessage={t("dairyManureStorageForm.progressBar.successMessage")}
                size={8}
              />
            )}

            {keyedManureStorage.entries.map((manure, index) => {
              const selectGroupWithIndex = `${selectGroupName}-${index}`;

              return (
                <Flex key={manure.clientKey} vertical gap={16}>
                  <Flex justify="space-between" align="center" vertical={false}>
                    <Typography variant="h5" style={{ flex: 1 }}>
                      {t("dairyManureStorageForm.storageNumber", { count: index + 1 })}
                    </Typography>
                    <IconButton
                      type="tertiary"
                      disabled={readOnly}
                      dataTestId={`delete-manure-storage-${index}`}
                      size="small"
                      iconName="tabler/trash"
                      onClick={() => keyedManureStorage.actions.remove(manure.clientKey)}
                    />
                  </Flex>
                  <Select
                    showSearch={true}
                    disabled={readOnly}
                    name={`${selectGroupName}-${manure.clientKey}`}
                    dataTestId={selectGroupWithIndex}
                    label={t("dairyManureStorageForm.question.manureStorageTypeQuestionInput")}
                    rules={[{ required: true, message: "" }]}
                    initialValue={dairyManureStorageQuery?.find((m) => m.id === manure.manureStorageId)?.name || ""}
                    value={dairyManureStorageQuery?.find((m) => m.id === manure.manureStorageId)?.name || ""}
                    onChange={(v) => {
                      const matchingPractice = dairyManureStorageQuery?.find((m) => m.name === v);
                      keyedManureStorage.actions.update(manure.clientKey, { manureStorageId: matchingPractice ? matchingPractice.id : "" });
                    }}
                    dropdownStyle={{ zIndex: 10010 }} // The modal presenting us has a z-index of 10k
                  >
                    {(dairyManureStorageQuery ?? []).map((dairyManureStorage) => {
                      // Check if this storage ID is already used in another item
                      const isAlreadyUsed = keyedManureStorage.entries.some(
                        (m) => m.manureStorageId === dairyManureStorage.id && m.manureStorageId !== manure.manureStorageId
                      );

                      return (
                        <Option
                          disabled={readOnly || isAlreadyUsed}
                          key={`${selectGroupWithIndex}-${dairyManureStorage.id}`}
                          data-testid={`${selectGroupWithIndex}-${dairyManureStorage.id}`}
                          value={dairyManureStorage.name}
                        >
                          {t(`${DAIRY_PRACTICE_SLUG.MANURE_STORAGE}.${dairyManureStorage.translationSlug}`, { ns: "dairy" }) || dairyManureStorage.name}
                        </Option>
                      );
                    })}
                  </Select>

                  <InputNumber
                    key={`amountStoredPct-${manure.clientKey}-${manure.manureStorageId}`}
                    disabled={readOnly}
                    name={`amountStoredPct-${manure.clientKey}`}
                    dataTestId="footprinting-manure-storage-pct-input"
                    label={t("dairyManureStorageForm.question.manureStoragePctQuestionInput")}
                    suffix="%"
                    value={formatFractionInput(manure.amountStoredPct)}
                    initialValue={formatFractionInput(manure.amountStoredPct)}
                    onChange={(v) => keyedManureStorage.actions.update(manure.clientKey, { amountStoredPct: formatPercentageInput(v) })}
                    validateFirst
                    rules={[
                      { required: true, message: t("dairyManureStorageForm.error.validPercentage") },
                      { type: "number" },
                      {
                        validator: (_: unknown, v: number) => {
                          if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                            return Promise.reject(new Error(t("dairyManureStorageForm.error.validPercentage")));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  />
                </Flex>
              );
            })}

            <Button
              type="text"
              dataTestId="add-manure-storage-button"
              disabled={readOnly}
              onClick={() => {
                keyedManureStorage.actions.add(manureStoragePlaceholder);
              }}
            >
              <Icon iconName="klim/plus" className="icon inline" /> {t("dairyManureStorageForm.add")}
            </Button>
          </Flex>
        </Card>

        <Card border className="padding-200">
          <Flex vertical gap={16}>
            <Flex vertical gap={8}>
              <Typography variant="h4">{t("dairyManureStorageForm.gasTightStorageTitle")}</Typography>
              <Typography variant="paragraph-m" color="grey-600">
                {t("dairyManureStorageForm.gasTightStorageSubtitle")}
              </Typography>
            </Flex>
            <InputNumber
              disabled={readOnly}
              name="amountStoredGasTankPct"
              dataTestId="footprinting-manure-gas-tight-storage-input"
              label={t("dairyManureStorageForm.question.gasTightStorageTypeQuestionInput")}
              suffix="%"
              value={formatFractionInput(amountStoredGasTankPct)}
              initialValue={formatFractionInput(amountStoredGasTankPct)}
              onChange={(v) => setAmountStoredGasTankPct(formatPercentageInput(v))}
              validateFirst
              rules={[
                { required: true, message: t("dairyManureStorageForm.error.validPercentage") },
                { type: "number" },
                {
                  validator: (_: unknown, v: number) => {
                    if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                      return Promise.reject(new Error(t("dairyManureStorageForm.error.validPercentage")));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            />
          </Flex>
        </Card>
      </Flex>
    </Form>
  );
};

ManureStorageForm.Key = "dairy.manureStorage";
export { ManureStorageForm };
